import React from "react";

import {
  IDemoPersistedRoiRequest,
  duplicateDemoRoiRequest,
  isDemoRoiRequestIdUnique,
} from "../../roi-request-create/model/roi-request-create.storage";
import * as Styled from "./roi-request-duplicate.styled";

interface IRoiRequestDuplicateDialogProps {
  request: IDemoPersistedRoiRequest | null;
  fallbackRequests?: IDemoPersistedRoiRequest[];
  visible: boolean;
  onClose: () => void;
  // eslint-disable-next-line no-unused-vars
  onDuplicate(request: IDemoPersistedRoiRequest): void;
}

const duplicateDialogCloseIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      d="M15.8327 4.16699L4.16602 15.8337"
      stroke="#5A5959"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M4.16602 4.16699L15.8327 15.8337"
      stroke="#5A5959"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const RoiRequestDuplicateDialog = ({
  request,
  fallbackRequests = [],
  visible,
  onClose,
  onDuplicate,
}: IRoiRequestDuplicateDialogProps) => {
  const [requestIdInput, setRequestIdInput] = React.useState("");
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!visible) {
      setRequestIdInput("");
      setError("");
    }
  }, [visible]);

  const handleClose = React.useCallback(() => {
    setRequestIdInput("");
    setError("");
    onClose();
  }, [onClose]);

  const handleSubmit = React.useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();

      if (!request || request.state !== "COMPLETED") {
        setError("Only completed ROI requests can be duplicated.");
        return;
      }

      const nextRequestId = Number.parseInt(requestIdInput, 10);

      if (!Number.isFinite(nextRequestId) || nextRequestId <= 0) {
        setError("Enter a valid Request ID.");
        return;
      }

      if (
        !isDemoRoiRequestIdUnique({
          requestId: nextRequestId,
          fallbackRequests,
        })
      ) {
        setError("Request ID must be unique.");
        return;
      }

      const duplicatedRequest = duplicateDemoRoiRequest({
        sourceRequest: request,
        requestId: nextRequestId,
        fallbackRequests,
      });

      if (!duplicatedRequest) {
        setError("Unable to duplicate the ROI request.");
        return;
      }

      onDuplicate(duplicatedRequest);
      handleClose();
    },
    [fallbackRequests, handleClose, onDuplicate, request, requestIdInput],
  );

  return (
    <Styled.ThemedDialog
      visible={visible}
      modal
      dismissableMask={false}
      blockScroll
      draggable={false}
      closeIcon={duplicateDialogCloseIcon}
      style={{ maxWidth: "546px", width: "100%" }}
      maskStyle={{
        background: "rgba(59, 68, 76, 0.6)",
      }}
      onHide={handleClose}
    >
      <Styled.DialogWrapper as="form" onSubmit={handleSubmit}>
        <Styled.DialogField>
          <Styled.DialogTitle>Duplicate ROI Request</Styled.DialogTitle>
          <Styled.DialogText>
            Enter a unique Request ID for this duplicate request.
          </Styled.DialogText>
        </Styled.DialogField>
        <Styled.DialogField $gap="4px">
          <Styled.DialogLabel htmlFor="duplicate-request-id">
            Request ID
          </Styled.DialogLabel>
          <Styled.DialogInput
            id="duplicate-request-id"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={requestIdInput}
            onChange={(event) => {
              setRequestIdInput(event.target.value.replace(/\D/g, ""));
              if (error) {
                setError("");
              }
            }}
          />
          {error ? <Styled.DialogError>{error}</Styled.DialogError> : null}
        </Styled.DialogField>
        <Styled.DialogActions>
          <Styled.SecondaryDialogButton type="button" onClick={handleClose}>
            Cancel
          </Styled.SecondaryDialogButton>
          <Styled.PrimaryDialogButton
            type="submit"
            disabled={requestIdInput.length === 0}
          >
            Duplicate
          </Styled.PrimaryDialogButton>
        </Styled.DialogActions>
      </Styled.DialogWrapper>
    </Styled.ThemedDialog>
  );
};
